import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  ArrowLeft,
  Edit,
  Building2,
  Users,
  CalendarCheck,
  Moon,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Settings,
  BookOpen,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { StatCard } from "@/components/admin/StatCard";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { NightCapBar } from "@/components/admin/NightCapBar";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import { ActivityFeed } from "@/components/admin/ActivityFeed";
import type { AuditLogItem } from "@/components/admin/ActivityFeed";
import {
  getBuildingById,
  getBuildingUnits,
  getBuildingBookings,
  getBuildingHosts,
  getAuditLogs,
} from "@/lib/admin/queries";
import {
  updateBuilding,
  suspendBuilding,
  deleteBuilding,
  updateComplianceRule,
  deactivateUnit,
} from "@/lib/admin/actions";
import type { Organization, Unit, Booking, Host, StrataConfig } from "@/types/database";
import { useAuth } from "@/contexts/AuthContext";
import type { DataTableColumn } from "@/components/admin/DataTable";

type EditFormValues = {
  name: string;
  address: string;
};

type ConfirmActionType = "suspend" | "delete" | null;

export default function BuildingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";

  const defaultTab = searchParams.get("tab") ?? "units";

  const [editOpen, setEditOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmActionType>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [unitsPage, setUnitsPage] = useState(1);
  const [bookingsPage, setBookingsPage] = useState(1);

  // Compliance inline edit state
  const [editingConfig, setEditingConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState<Partial<StrataConfig>>({});

  const buildingQuery = useQuery({
    queryKey: ["admin", "building", id],
    queryFn: () => getBuildingById(id!),
    enabled: !!id,
  });

  const unitsQuery = useQuery({
    queryKey: ["admin", "building-units", id, unitsPage],
    queryFn: () => getBuildingUnits(id!, unitsPage),
    enabled: !!id,
  });

  const bookingsQuery = useQuery({
    queryKey: ["admin", "building-bookings", id, bookingsPage],
    queryFn: () => getBuildingBookings(id!, bookingsPage),
    enabled: !!id,
  });

  const hostsQuery = useQuery({
    queryKey: ["admin", "building-hosts", id],
    queryFn: () => getBuildingHosts(id!),
    enabled: !!id,
  });

  const auditQuery = useQuery({
    queryKey: ["admin", "building-audit", id],
    queryFn: () => getAuditLogs({ search: id }, 1, 30),
    enabled: !!id,
  });

  const building = buildingQuery.data?.data as (Organization & { config?: StrataConfig; admin?: { full_name?: string; email?: string } }) | null | undefined;
  const strataConfig = building?.config as StrataConfig | undefined;

  const editForm = useForm<EditFormValues>();
  useEffect(() => {
    if (building) {
      editForm.reset({ name: building.name, address: building.address ?? "" });
    }
  }, [building, editForm]);

  useEffect(() => {
    if (strataConfig) {
      setConfigDraft({
        max_nights_per_year: strataConfig.max_nights_per_year,
        min_stay_nights: strataConfig.min_stay_nights,
        max_guest_count: strataConfig.max_guest_count,
        require_insurance: strataConfig.require_insurance,
        require_council_approval_per_booking: strataConfig.require_council_approval_per_booking,
        require_provincial_registration: strataConfig.require_provincial_registration,
        annual_permit_required: strataConfig.annual_permit_required,
        max_consecutive_nights: strataConfig.max_consecutive_nights,
        no_parties_events: strataConfig.no_parties_events,
        pets_allowed_str: strataConfig.pets_allowed_str,
        str_policy: strataConfig.str_policy,
      });
    }
  }, [strataConfig]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Organization>) => updateBuilding(id!, data, actorId),
    onSuccess: () => {
      toast.success("Building updated");
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "building", id] });
    },
    onError: () => toast.error("Failed to update building"),
  });

  const updateConfigMutation = useMutation({
    mutationFn: (data: Partial<StrataConfig>) => updateComplianceRule(id!, data, actorId),
    onSuccess: () => {
      toast.success("Compliance rules saved");
      setEditingConfig(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "building", id] });
    },
    onError: () => toast.error("Failed to save compliance rules"),
  });

  const deactivateUnitMutation = useMutation({
    mutationFn: (unitId: string) => deactivateUnit(unitId, actorId),
    onSuccess: () => {
      toast.success("Unit deactivated");
      queryClient.invalidateQueries({ queryKey: ["admin", "building-units", id] });
    },
    onError: () => toast.error("Failed to deactivate unit"),
  });

  async function handleConfirm() {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      if (confirmAction === "suspend") {
        const res = await suspendBuilding(id!, actorId);
        if (res.success) {
          toast.success("Building suspended");
          queryClient.invalidateQueries({ queryKey: ["admin", "building", id] });
        } else {
          toast.error(res.error ?? "Failed");
        }
      } else if (confirmAction === "delete") {
        const res = await deleteBuilding(id!, actorId);
        if (res.success) {
          toast.success("Building deleted");
          navigate("/admin/buildings");
        } else {
          toast.error(res.error ?? "Failed");
        }
      }
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
    }
  }

  const unitColumns: DataTableColumn<Unit & Record<string, unknown>>[] = [
    {
      key: "unit_number",
      label: "Unit #",
      render: (row) => (
        <span className="font-semibold text-gray-900">{row.unit_number as string}</span>
      ),
    },
    {
      key: "owner_name",
      label: "Owner",
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{row.owner_name as string}</p>
          <p className="text-xs text-gray-400">{(row.owner_email as string | null) ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "nights",
      label: "Nights Used / Cap",
      render: (row) => {
        const host = (row as Unit & { host?: { nights_used?: number; night_limit?: number } | null }).host ?? null;
        const used = host?.nights_used ?? 0;
        const cap = host?.night_limit ?? 90;
        return (
          <div className="min-w-[140px]">
            <NightCapBar used={used} cap={cap} showLabel />
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status as string} size="sm" />,
    },
    {
      key: "str_approved",
      label: "STR Approved",
      render: (row) => (
        <span className={`text-xs font-medium ${row.str_approved ? "text-green-600" : "text-red-500"}`}>
          {row.str_approved ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => deactivateUnitMutation.mutate(row.id as string)}
          disabled={deactivateUnitMutation.isPending}
        >
          Deactivate
        </Button>
      ),
    },
  ];

  const bookingColumns: DataTableColumn<Booking & Record<string, unknown>>[] = [
    {
      key: "check_in",
      label: "Check-In",
      render: (row) => (
        <span className="text-sm tabular-nums">
          {new Date(row.check_in as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "check_out",
      label: "Check-Out",
      render: (row) => (
        <span className="text-sm tabular-nums">
          {new Date(row.check_out as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "unit",
      label: "Unit",
      render: (row) => {
        const unit = row.unit as { unit_number?: string } | undefined;
        return <span className="text-sm">{unit?.unit_number ?? "—"}</span>;
      },
    },
    {
      key: "guest_name",
      label: "Guest",
      render: (row) => <span className="text-sm">{row.guest_name as string}</span>,
    },
    {
      key: "nights",
      label: "Nights",
      render: (row) => <span className="text-sm tabular-nums">{row.nights as number}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status as string} size="sm" />,
    },
  ];

  const hostColumns: DataTableColumn<Host & Record<string, unknown>>[] = [
    {
      key: "profile",
      label: "Host",
      render: (row) => {
        const p = row.profile as { full_name?: string; email?: string } | undefined;
        return (
          <div>
            <p className="text-sm font-medium text-gray-900">{p?.full_name ?? "—"}</p>
            <p className="text-xs text-gray-400">{p?.email ?? "—"}</p>
          </div>
        );
      },
    },
    {
      key: "nights_used",
      label: "Nights Used",
      render: (row) => (
        <span className="text-sm tabular-nums">{row.nights_used as number}</span>
      ),
    },
    {
      key: "night_limit",
      label: "Night Cap",
      render: (row) => (
        <span className="text-sm tabular-nums">{row.night_limit as number}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status as string} size="sm" />,
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/users/${row.user_id as string}`)}
        >
          View User
        </Button>
      ),
    },
  ];

  const auditItems: AuditLogItem[] = (auditQuery.data?.data ?? []).map((item) => ({
    id: item.id,
    action: item.action,
    entity_type: item.entity ?? "",
    entity_id: "",
    actor_name: (item as { actor?: { full_name?: string } }).actor?.full_name ?? item.user_name ?? "System",
    created_at: item.created_at,
    details: null,
  }));

  const totalBookings = bookingsQuery.data?.count ?? 0;
  const totalHosts = hostsQuery.data?.data.length ?? 0;
  const totalUnits = building?.total_units ?? 0;

  if (buildingQuery.isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!building) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="text-center py-20 text-gray-400">Building not found.</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      {/* Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <Building2 className="h-7 w-7 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900">{building.name}</h2>
                <StatusBadge status="active" size="sm" />
                <Badge variant="outline" className="text-xs capitalize">
                  {building.plan.replace(/_/g, " ")}
                </Badge>
              </div>
              {building.address && (
                <p className="text-sm text-gray-500 mt-0.5">{building.address}</p>
              )}
              {building.admin && (
                <p className="text-xs text-gray-400 mt-0.5">
                  Manager: {building.admin.full_name ?? building.admin.email ?? "—"}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="whitespace-nowrap" onClick={() => setEditOpen(true)}>
                <Edit className="h-4 w-4 mr-1.5" /> Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction("suspend")}
              >
                <ShieldOff className="h-4 w-4 mr-1.5" /> Suspend
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Units"
          value={totalUnits}
          icon={<Building2 className="h-5 w-5" />}
          color="blue"
        />
        <StatCard
          title="Active Hosts"
          value={totalHosts}
          icon={<Users className="h-5 w-5" />}
          color="green"
        />
        <StatCard
          title="Bookings This Month"
          value={totalBookings}
          icon={<CalendarCheck className="h-5 w-5" />}
          color="purple"
        />
        <StatCard
          title="Nights Tracked"
          value={
            (hostsQuery.data?.data ?? []).reduce(
              (acc, h) => acc + (h.nights_used ?? 0),
              0
            )
          }
          icon={<Moon className="h-5 w-5" />}
          color="amber"
        />
        <StatCard
          title="Compliance Rate"
          value="100%"
          icon={<ShieldCheck className="h-5 w-5" />}
          color="green"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab}>
        <TabsList className="mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="units" className="whitespace-nowrap">
            <Building2 className="h-4 w-4 mr-1.5" /> Units
          </TabsTrigger>
          <TabsTrigger value="bookings" className="whitespace-nowrap">
            <BookOpen className="h-4 w-4 mr-1.5" /> Bookings
          </TabsTrigger>
          <TabsTrigger value="compliance" className="whitespace-nowrap">
            <ShieldCheck className="h-4 w-4 mr-1.5" /> Compliance Rules
          </TabsTrigger>
          <TabsTrigger value="hosts" className="whitespace-nowrap">
            <Users className="h-4 w-4 mr-1.5" /> Hosts
          </TabsTrigger>
          <TabsTrigger value="audit" className="whitespace-nowrap">
            <ClipboardList className="h-4 w-4 mr-1.5" /> Audit Log
          </TabsTrigger>
          <TabsTrigger value="settings" className="whitespace-nowrap">
            <Settings className="h-4 w-4 mr-1.5" /> Settings
          </TabsTrigger>
        </TabsList>

        {/* Units */}
        <TabsContent value="units">
          <Card>
            <CardHeader>
              <CardTitle>Units</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={unitColumns as DataTableColumn[]}
                data={(unitsQuery.data?.data ?? []) as Record<string, unknown>[]}
                loading={unitsQuery.isLoading}
                totalCount={unitsQuery.data?.count}
                page={unitsPage}
                pageSize={20}
                onPageChange={setUnitsPage}
                emptyMessage="No units found for this building."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bookings */}
        <TabsContent value="bookings">
          <Card>
            <CardHeader>
              <CardTitle>Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={bookingColumns as DataTableColumn[]}
                data={(bookingsQuery.data?.data ?? []) as Record<string, unknown>[]}
                loading={bookingsQuery.isLoading}
                totalCount={bookingsQuery.data?.count}
                page={bookingsPage}
                pageSize={20}
                onPageChange={setBookingsPage}
                emptyMessage="No bookings for this building."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Rules */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Compliance Rules</CardTitle>
                <div className="flex gap-2">
                  {editingConfig ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={() => setEditingConfig(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="whitespace-nowrap"
                        disabled={updateConfigMutation.isPending}
                        onClick={() => updateConfigMutation.mutate(configDraft)}
                      >
                        {updateConfigMutation.isPending ? "Saving…" : "Save Rules"}
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="whitespace-nowrap" onClick={() => setEditingConfig(true)}>
                      <Edit className="h-4 w-4 mr-1.5" /> Edit Rules
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!strataConfig ? (
                <div className="text-center py-10 text-sm text-gray-400">
                  No compliance rules configured yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {/* Night cap */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Max Nights per Year
                    </Label>
                    {editingConfig ? (
                      <Input
                        type="number"
                        min={1}
                        value={configDraft.max_nights_per_year ?? ""}
                        onChange={(e) =>
                          setConfigDraft((d) => ({
                            ...d,
                            max_nights_per_year: Number(e.target.value),
                          }))
                        }
                      />
                    ) : (
                      <p className="text-sm text-gray-900 font-medium">
                        {strataConfig.max_nights_per_year} nights
                      </p>
                    )}
                  </div>

                  {/* Min stay */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Min Stay Nights
                    </Label>
                    {editingConfig ? (
                      <Input
                        type="number"
                        min={1}
                        value={configDraft.min_stay_nights ?? ""}
                        onChange={(e) =>
                          setConfigDraft((d) => ({
                            ...d,
                            min_stay_nights: Number(e.target.value),
                          }))
                        }
                      />
                    ) : (
                      <p className="text-sm text-gray-900 font-medium">
                        {strataConfig.min_stay_nights} night(s)
                      </p>
                    )}
                  </div>

                  {/* Max guest count */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Max Guest Count
                    </Label>
                    {editingConfig ? (
                      <Input
                        type="number"
                        min={1}
                        value={configDraft.max_guest_count ?? ""}
                        onChange={(e) =>
                          setConfigDraft((d) => ({
                            ...d,
                            max_guest_count: Number(e.target.value),
                          }))
                        }
                      />
                    ) : (
                      <p className="text-sm text-gray-900 font-medium">
                        {strataConfig.max_guest_count} guests
                      </p>
                    )}
                  </div>

                  {/* Max consecutive nights */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Max Consecutive Nights
                    </Label>
                    {editingConfig ? (
                      <Input
                        type="number"
                        min={1}
                        value={configDraft.max_consecutive_nights ?? ""}
                        onChange={(e) =>
                          setConfigDraft((d) => ({
                            ...d,
                            max_consecutive_nights: e.target.value ? Number(e.target.value) : null,
                          }))
                        }
                        placeholder="No limit"
                      />
                    ) : (
                      <p className="text-sm text-gray-900 font-medium">
                        {strataConfig.max_consecutive_nights ?? "No limit"}
                      </p>
                    )}
                  </div>

                  {/* STR Policy */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      STR Policy
                    </Label>
                    {editingConfig ? (
                      <Select
                        value={configDraft.str_policy ?? strataConfig.str_policy}
                        onValueChange={(v) =>
                          setConfigDraft((d) => ({
                            ...d,
                            str_policy: v as StrataConfig["str_policy"],
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="allow_all">Allow All</SelectItem>
                          <SelectItem value="principal_residence_only">
                            Principal Residence Only
                          </SelectItem>
                          <SelectItem value="banned">Banned</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-gray-900 font-medium capitalize">
                        {strataConfig.str_policy.replace(/_/g, " ")}
                      </p>
                    )}
                  </div>

                  {/* Toggle: Approval Required */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 leading-snug">Council Approval per Booking</p>
                      <p className="text-xs text-gray-400">Requires council sign-off for each booking</p>
                    </div>
                    <Switch
                      checked={
                        editingConfig
                          ? (configDraft.require_council_approval_per_booking ?? false)
                          : strataConfig.require_council_approval_per_booking
                      }
                      onCheckedChange={(v) =>
                        editingConfig &&
                        setConfigDraft((d) => ({
                          ...d,
                          require_council_approval_per_booking: v,
                        }))
                      }
                      disabled={!editingConfig}
                    />
                  </div>

                  {/* Toggle: Provincial Registration */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 leading-snug">Provincial Registration Required</p>
                      <p className="text-xs text-gray-400">Hosts must have a valid STR registration number</p>
                    </div>
                    <Switch
                      checked={
                        editingConfig
                          ? (configDraft.require_provincial_registration ?? false)
                          : strataConfig.require_provincial_registration
                      }
                      onCheckedChange={(v) =>
                        editingConfig &&
                        setConfigDraft((d) => ({
                          ...d,
                          require_provincial_registration: v,
                        }))
                      }
                      disabled={!editingConfig}
                    />
                  </div>

                  {/* Toggle: Annual Permit */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 leading-snug">Annual Permit Required</p>
                      <p className="text-xs text-gray-400">Hosts must renew permit annually</p>
                    </div>
                    <Switch
                      checked={
                        editingConfig
                          ? (configDraft.annual_permit_required ?? false)
                          : strataConfig.annual_permit_required
                      }
                      onCheckedChange={(v) =>
                        editingConfig &&
                        setConfigDraft((d) => ({ ...d, annual_permit_required: v }))
                      }
                      disabled={!editingConfig}
                    />
                  </div>

                  {/* Toggle: Insurance */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 leading-snug">Insurance Required</p>
                      <p className="text-xs text-gray-400">Hosts must carry STR liability insurance</p>
                    </div>
                    <Switch
                      checked={
                        editingConfig
                          ? (configDraft.require_insurance ?? false)
                          : strataConfig.require_insurance
                      }
                      onCheckedChange={(v) =>
                        editingConfig &&
                        setConfigDraft((d) => ({ ...d, require_insurance: v }))
                      }
                      disabled={!editingConfig}
                    />
                  </div>

                  {/* Toggle: No Parties */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 leading-snug">No Parties / Events</p>
                    </div>
                    <Switch
                      checked={
                        editingConfig
                          ? (configDraft.no_parties_events ?? false)
                          : strataConfig.no_parties_events
                      }
                      onCheckedChange={(v) =>
                        editingConfig &&
                        setConfigDraft((d) => ({ ...d, no_parties_events: v }))
                      }
                      disabled={!editingConfig}
                    />
                  </div>

                  {/* Toggle: Pets Allowed */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 leading-snug">Pets Allowed</p>
                    </div>
                    <Switch
                      checked={
                        editingConfig
                          ? (configDraft.pets_allowed_str ?? false)
                          : strataConfig.pets_allowed_str
                      }
                      onCheckedChange={(v) =>
                        editingConfig &&
                        setConfigDraft((d) => ({ ...d, pets_allowed_str: v }))
                      }
                      disabled={!editingConfig}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hosts */}
        <TabsContent value="hosts">
          <Card>
            <CardHeader>
              <CardTitle>Hosts</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={hostColumns as DataTableColumn[]}
                data={(hostsQuery.data?.data ?? []) as Record<string, unknown>[]}
                loading={hostsQuery.isLoading}
                emptyMessage="No hosts registered in this building."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityFeed
                items={auditItems}
                loading={auditQuery.isLoading}
                maxItems={30}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Building Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={editForm.handleSubmit((data) =>
                    updateMutation.mutate({ name: data.name, address: data.address })
                  )}
                  className="max-w-md space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="bs-name">Building Name <span className="text-red-500">*</span></Label>
                    <Input id="bs-name" {...editForm.register("name", { required: true })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bs-address">Address</Label>
                    <Input id="bs-address" {...editForm.register("address")} />
                  </div>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? "Saving…" : "Save Changes"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-700">Danger Zone</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-red-700">Suspend Building</p>
                    <p className="text-xs text-red-500">
                      Suspends all hosts and units. Bookings will not be processed.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700 hover:bg-red-100 whitespace-nowrap"
                    onClick={() => setConfirmAction("suspend")}
                  >
                    <ShieldOff className="h-4 w-4 mr-1.5" /> Suspend
                  </Button>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-red-700">Delete Building</p>
                    <p className="text-xs text-red-500">
                      Permanently removes all data. This action cannot be undone.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={() => setConfirmAction("delete")}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-[400px] sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit Building</SheetTitle>
          </SheetHeader>
          <form
            onSubmit={editForm.handleSubmit((data) =>
              updateMutation.mutate({ name: data.name, address: data.address })
            )}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="es-name">Building Name <span className="text-red-500">*</span></Label>
              <Input id="es-name" {...editForm.register("name", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="es-address">Address</Label>
              <Input id="es-address" {...editForm.register("address")} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Confirm Modal */}
      <ConfirmModal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        loading={confirmLoading}
        title={
          confirmAction === "delete" ? "Delete Building" : "Suspend Building"
        }
        description={
          confirmAction === "delete"
            ? `Permanently delete "${building.name}"? All units, hosts, and bookings will be removed. This cannot be undone.`
            : `Suspend "${building.name}"? All active hosts and units will be suspended.`
        }
        requireTyping={confirmAction === "delete"}
        variant={confirmAction === "delete" ? "destructive" : "warning"}
      />
    </div>
  );
}
